import {useEffect, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import MUITable from '../../modules/common/MUITable'
import TableLoading from '../../../_metronic/layout/components/Common/TableSkeletonLoading'
import {getAllEnrichmentPhrasesRequest} from '../../../api/endpoints/enrichmentPhrases'
import PhrasesModal from './components/PhrasesModal'

export interface Phrase {
  id: number | string
  content: string
}

// one row per subchannel (service), holding all of its trigger phrases
export interface PhraseRow {
  serviceId: number
  channel: string
  subchannel: string
  phrases: Phrase[]
}

const SHOWN_PHRASES = 6

// data.data is {channels: [{id, name, sub_channels: [{id, name, phrases: [{id, content}]}]}], total, lastPage}.
// A subchannel with no phrases yet still gets a row, so the admin can add its first ones.
const toRows = (res: any): PhraseRow[] => {
  const rows: PhraseRow[] = []
  for (const c of Array.isArray(res?.channels) ? res.channels : []) {
    for (const s of Array.isArray(c?.sub_channels) ? c.sub_channels : []) {
      rows.push({
        serviceId: s.id,
        channel: c.name ?? '',
        subchannel: s.name ?? '',
        phrases: (Array.isArray(s.phrases) ? s.phrases : []).map((p: any) => ({
          id: p.id,
          content: p.content ?? '',
        })),
      })
    }
  }
  return rows
}

const Howie = () => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<PhraseRow | null>(null)
  // subchannels whose phrases are all shown ("+N more" clicked)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const toggleExpanded = (serviceId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(serviceId) ? next.delete(serviceId) : next.add(serviceId)
      return next
    })

  const {data, isLoading, isError} = useQuery({
    queryKey: ['howiePhrases', {page, perPage, q: search}],
    queryFn: () => getAllEnrichmentPhrasesRequest({page, perPage, q: search}),
    select: (res: any) => ({
      rows: toRows(res),
      pagination: {total: res?.total ?? 0, lastPage: res?.lastPage ?? res?.last_page ?? 1},
    }),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  })

  // keep the open modal in step with the reloaded page after a save
  useEffect(() => {
    if (!data) return
    setSelected((prev) =>
      prev ? data.rows.find((r) => r.serviceId === prev.serviceId) ?? prev : prev
    )
  }, [data])

  const columns = [
    {id: 'channel', label: 'Channel', align: 'left' as const},
    {id: 'subchannel', label: 'Subchannel', align: 'left' as const},
    {id: 'phrases', label: 'Trigger phrases', align: 'left' as const},
    {id: 'actions', label: 'Actions', align: 'center' as const},
  ]

  const tableData = {
    table: {
      columns,
      rows: (data?.rows ?? []).map((r) => ({
        channel: <span className='text-nowrap'>{r.channel || '-'}</span>,
        subchannel: <span className='fw-bold'>{r.subchannel || '-'}</span>,
        phrases:
          r.phrases.length === 0 ? (
            <span className='text-muted'>No phrases yet</span>
          ) : (
            <div className='d-flex flex-wrap gap-2'>
              {(expanded.has(r.serviceId) ? r.phrases : r.phrases.slice(0, SHOWN_PHRASES)).map(
                (p) => (
                  <span key={p.id} className='badge badge-light fw-normal'>
                    {p.content}
                  </span>
                )
              )}
              {r.phrases.length > SHOWN_PHRASES && (
                <button
                  type='button'
                  className='badge badge-light-primary border-0'
                  style={{cursor: 'pointer'}}
                  onClick={() => toggleExpanded(r.serviceId)}
                >
                  {expanded.has(r.serviceId)
                    ? 'Show less'
                    : `+${r.phrases.length - SHOWN_PHRASES} more`}
                </button>
              )}
            </div>
          ),
        actions: (
          <button
            onClick={() => setSelected(r)}
            type='button'
            className='btn btn-sm btn-primary'
            style={{padding: '0.5rem 1rem', fontSize: '0.875rem'}}
          >
            Edit
          </button>
        ),
      })),
    },
    pagination: data?.pagination ?? {total: 0, lastPage: 1},
  }

  return (
    <>
      <div className='row mb-5'>
        <div className='col-md-8'>
          <h2 className='py-3 mb-0'>Howie trigger phrases</h2>
          <div className='text-muted'>
            Everyday phrases that lead Howie to the right channel and subchannel.
          </div>
        </div>
        <div className='col-md-4 align-self-center text-end'>
          <input
            type='search'
            onChange={(e) => {
              // like the other lists: search from 3 characters, or clear
              if (e.target.value.length >= 3 || e.target.value === '') {
                setSearch(e.target.value)
                setPage(1)
              }
            }}
            placeholder='Search'
            className='form-control form-control-sm'
          />
        </div>
      </div>
      <div className='card mb-5 mb-xl-10 p-5'>
        {isLoading ? (
          <TableLoading />
        ) : isError ? (
          <h2 className='text-center text-danger my-5'>Not able to fetch data at the moment</h2>
        ) : tableData.table.rows.length === 0 ? (
          <div className='text-muted text-center my-5'>No phrases found</div>
        ) : (
          <MUITable
            data={tableData}
            page={page}
            perPage={perPage}
            setPage={setPage}
            setPerPage={(n: number) => {
              setPerPage(n)
              setPage(1)
            }}
          />
        )}
      </div>
      <PhrasesModal
        row={selected}
        onClose={() => setSelected(null)}
        onSaved={() => queryClient.invalidateQueries({queryKey: ['howiePhrases']})}
      />
    </>
  )
}

export default Howie
